import React, { useState, useEffect, use } from "react";
import { Form, ListGroup, Row, Spinner, Button } from "react-bootstrap";
import { getHeaders, GoogleMapsAPIKey, dataConnect } from "../firebaseConfig";
import { venueSearch } from "@firebasegen/default-connector";
import { resolvePlace, placeFromGoogle } from "../services/placeService";
import TextField from "@mui/material/TextField";
import LocationSearchField from "./LocationSearchField";
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';

const VenueSearchField = ({
    venue,
    onChange,
    artistId,
    fieldKey,
    showCreateVenue, // Default to false if not provided
}) => {
    const [venueName, setVenueName] = useState(venue);
    const [debouncedSearch, setDebouncedSearch] = useState(venue);
    const [venueSelected, setVenueSelected] = useState(false);
    const [venueId, setVenueId] = useState("");
    const [venueCoords, setVenueCoords] = useState(null);
    const [venueResults, setVenueResults] = useState([]);
    const [showVenueList, setShowVenueList] = useState(false);
    const [venueLoading, setVenueLoading] = useState(false);
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [capacity, setCapacity] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [website, setWebsite] = useState("");

    useEffect(() => {
        let isInitial = true;
        const handler = setTimeout(() => {
            if (venueSelected) return;
            // Only run debounce if user has typed
            if (hasUserTyped) {
                console.log("Debounced search:", venueName);
                setDebouncedSearch(venueName);
            }
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [venueName, venueSelected, hasUserTyped]);

    useEffect(() => {
        showCreateVenue(showCreateModal);
    }, [showCreateModal]);


    useEffect(() => {
        if (venue && venue.name) {
            setVenueName(venue.name);
        }
    }, [venue]);


    useEffect(() => {
        if (debouncedSearch && hasUserTyped) {
            fetchVenues(debouncedSearch);
        }
    }, [debouncedSearch, hasUserTyped]);

    const handleVenueSearch = async (searchTerm) => {
        setHasUserTyped(true);
        console.log("Search venue:", searchTerm);
        setVenueSelected(false);
        setVenueName(searchTerm);
        setVenueId("");
        setVenueCoords(null);
    };

    const fetchVenues = async (searchTerm) => {
        if (searchTerm.length < 3) {
            setVenueResults([]);
            setShowVenueList(false);
            return;
        }

        setVenueLoading(true);
        try {
            const { data } = await venueSearch(dataConnect, { s: searchTerm });
            if (data.venues.length > 0) {
                setVenueResults(data.venues);
                setShowVenueList(true);
            } else {
                // Instead of calling searchGooglePlaces, show "Create new venue" option
                setVenueResults([]);
                setShowVenueList(true);
            }
        } catch (error) {
            console.error("Error searching venue:", error);
        }
        setVenueLoading(false);
    };

    const handleSelectVenue = async (venueData) => {
        setVenueSelected(true);
        setShowVenueList(false);
        setVenueResults([]);

        if (venueData.id !== undefined) {
            setVenueName(venueData.name);
            setVenueId(venueData.id);
            setVenueCoords(venueData.location.latitude && venueData.location.longitude ? { lat: venueData.location.latitude, lng: venueData.location.longitude } : null);
            console.log("on change" + venueData.id);
            onChange(venueData.id, venueData); // Pass full venue object
            return;
        }

        try {
            // Reuses the venue if this place is already in Tourminator
            const place = placeFromGoogle(venueData);
            const { venueId: newVenueId, venueName: newVenueName } = await resolvePlace(place, { artistId, asVenue: true });

            setVenueCoords(place.latitude != null && place.longitude != null ? { lat: place.latitude, lng: place.longitude } : null);
            setVenueName(newVenueName || venueData.name);
            setVenueId(newVenueId);
            onChange(newVenueId);
        } catch (error) {
            console.error("Error creating venue:", error);
        }
    };

    const searchGooglePlaces = async (searchTerm) => {
        try {
            if (!GoogleMapsAPIKey) {
                console.error("Google Maps API Key is missing! Check your .env file.");
                return;
            }

            const response = await fetch(`https://places.googleapis.com/v1/places:searchText`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Goog-Api-Key": GoogleMapsAPIKey,
                    "X-Goog-FieldMask": "places.id,places.displayName,places.formattedAddress,places.location,places.addressComponents"
                },
                body: JSON.stringify({
                    textQuery: searchTerm
                })
            });

            const data = await response.json();
            if (data.places && data.places.length > 0) {
                const results = data.places.map((place) => ({
                    google: true,
                    name: place.displayName.text,
                    formatted_address: place.formattedAddress,
                    city: extractAddressComponent(place.addressComponents, "locality") || extractAddressComponent(place.addressComponents, "postal_town") || extractAddressComponent(place.addressComponents, "administrative_area_level_3"),
                    zipCode: extractAddressComponent(place.addressComponents, "postal_code"),
                    country: extractAddressComponent(place.addressComponents, "country"),
                    geometry: { location: { lat: place.location.latitude, lng: place.location.longitude } },
                    place_id: place.id
                }));

                for (let place of results) {
                    const lat = place.geometry.location.lat;
                    const lng = place.geometry.location.lng;
                    const timestamp = Math.floor(Date.now() / 1000);
                    try {
                        const tzRes = await fetch(`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${GoogleMapsAPIKey}`);
                        const tzData = await tzRes.json();
                        if (tzData.status === "OK") {
                            place.timezone = tzData.timeZoneId;
                        }
                    } catch (err) {
                        console.error("Error fetching timezone:", err);
                    }
                }

                setVenueResults(results);
                setShowVenueList(true);
            } else {
                setVenueResults([]);
            }
        } catch (error) {
            console.error("Error fetching Google Places:", error);
        }
    };

    const extractAddressComponent = (components, type) => {
        const component = components.find((c) => c.types.includes(type));
        return component ? component.longText : "";
    };

    const [venueLocationId, setVenueLocationId] = useState(null); // ID from LocationSearchField

    // ... (existing code)

    const handleCreateVenue = async () => {
        try {
            const { venueId: newVenueId, venueName: newVenueName } = await resolvePlace({
                name: venueName,
                latitude: venueCoords?.lat ?? null,
                longitude: venueCoords?.lng ?? null,
                capacity: parseInt(capacity, 10) || null,
                phoneNumber,
                website,
            }, { artistId, asVenue: true, locationId: venueLocationId });
            onChange(newVenueId, { id: newVenueId, name: newVenueName || venueName });
            setShowCreateModal(false);
            setShowVenueList(false);
        } catch (error) {
            console.error("Error in handleCreateVenue:", error);
        }
    };

    return (
        <span>
            <TextField
                type="text"
                fullWidth
                value={venueName}
                key={fieldKey}
                onChange={(e) => handleVenueSearch(e.target.value)}
                placeholder="Search or enter a venue"
            />
            {venueLoading && <Spinner animation="border" size="sm" />}
            {showVenueList && (
                <ListGroup className="venue-list">
                    {venueResults.length > 0 ? (
                        venueResults.map((result, index) => (
                            <ListGroup.Item key={index} onClick={() => handleSelectVenue(result)} className="venue-item">
                                {result.name} {result.formatted_address ? `(${result.formatted_address})` : ""}
                            </ListGroup.Item>
                        ))
                    ) : (
                        <></>
                    )}
                    <ListGroup.Item
                        key="create-new-venue"
                        onClick={() => setShowCreateModal(true)}
                        className="venue-item text-primary"
                        style={{ cursor: "pointer", fontWeight: "bold" }}
                    >
                        Create new venue
                    </ListGroup.Item>
                </ListGroup>
            )}
            <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        p: 3,
                    }}
                >
                    <Stack spacing={2}>
                        <Typography variant="h6" component="h2">
                            Create New Venue
                        </Typography>
                        <Divider />
                        <TextField
                            label="Venue Name"
                            value={venueName}
                            onChange={(e) => setVenueName(e.target.value)}
                            fullWidth
                        />                   <LocationSearchField onChange={(data) => {
                            if (data.id) setVenueLocationId(data.id);
                            setVenueCoords(data.location);
                            if (data.website) setWebsite(data.website);
                            if (data.phoneNumber) setPhoneNumber(data.phoneNumber);
                            if (data.capacity) setCapacity(data.capacity);
                            if (data.name) setVenueName(data.name);
                        }} />
                        <TextField
                            label="Capacity"
                            type="number"
                            value={capacity}
                            onChange={(e) => setCapacity(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Phone Number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Website"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            fullWidth
                        />
                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <Button variant="outlined" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </Button>
                            <LoadingButton variant="contained" onClick={handleCreateVenue}>
                                Create
                            </LoadingButton>
                        </Stack>
                    </Stack >
                </Box >
            </Modal >
        </span >
    );
};

export default VenueSearchField;
